import { useCallback, useState } from 'react';

import { collectAll } from '@atlassian/experience-tracker';

import { useAnalytics } from '../components/AnalyticsContext';

import { type UseTrackerStartOptions } from './types';

export function useExpTrackerStart(options: UseTrackerStartOptions) {
	const {
		id,
		name,
		timeout,
		isTriggerOnMount = true,
		subExperiences,
		latencySloMs,
		startAttributes,
		onSuccess,
	} = options;

	const analytics = useAnalytics();
	const tracker = analytics.expTracker();

	const triggerStartEvent = useCallback(() => {
		const options = {
			id: id,
			name,
			timeout,
			collect: undefined as any,
			attributes: startAttributes,
			onSuccess,
		};

		if (subExperiences) {
			options.collect = collectAll(subExperiences);
		}

		if (latencySloMs) {
			options.attributes = { latencySlo: latencySloMs };
		}

		tracker.start(options);
	}, [id, name, timeout, startAttributes, onSuccess, subExperiences, latencySloMs, tracker]);

	const triggerSuccessEvent = useCallback(() => {
		tracker.succeed({ name });
	}, [name, tracker]);

	const triggerFailEvent = useCallback(
		(error: Error) => {
			tracker.stopOnError({ name, error });
		},
		[name, tracker],
	);

	const triggerAbortEvent = useCallback(
		(reason: string) => {
			tracker.abort({ name, reason });
		},
		[name, tracker],
	);

	// we use 'useState' here because we want to trigger start event as soon as possible, before rendering component,
	useState(() => {
		// default behaviour: trigger start event when mounting.
		// "isTriggerOnMount=false" is allow to turn off the default behaviour.
		if (isTriggerOnMount) {
			triggerStartEvent();
		}
	});

	// allow to trigger start event whenever consumers want like inside onClick.
	return {
		start: triggerStartEvent,
		succeed: triggerSuccessEvent,
		abort: triggerAbortEvent,
		fail: triggerFailEvent,
	};
}
